import React from "react";
import { Link } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { Badge } from "../../../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { ClusterStatusBadge } from "./ClusterTable";
import {
  formatCount,
  formatDateTime,
  formatMix,
  formatTrend,
  eligibilityBadges,
  lifeAreaLabel,
  nearestDialogues,
  traceabilitySteps,
} from "./researchPulseFormat";

const Section = ({ title, children, description }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="text-base">{title}</CardTitle>
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const Field = ({ label, children }) => (
  <div>
    <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
    <dd className="text-sm">{children || "—"}</dd>
  </div>
);

export const dialogueHref = (dialogueId) =>
  `/admin/research-library/${encodeURIComponent(dialogueId)}`;

export const SignatureSection = ({ cluster }) => (
  <Section title="Signature">
    <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <Field label="Life areas">
        {(cluster.life_areas || []).map(lifeAreaLabel).join(", ")}
      </Field>
      <Field label="Situation family">{cluster.situation_family}</Field>
      <Field label="Topic">{cluster.topic}</Field>
      <Field label="Role">{cluster.role}</Field>
      <Field label="Populations">{(cluster.populations || []).join(", ")}</Field>
      <Field label="Evidence availability">{cluster.evidence_availability}</Field>
      <Field label="Frequency / members">
        {`${formatCount(cluster.frequency)} signals · ${formatCount(
          cluster.distinct_members
        )} members`}
      </Field>
      <Field label="Trend">
        {formatTrend(cluster.trend)}
        {cluster.trend?.rising ? " · rising" : ""}
      </Field>
      <Field label="Sources">{formatMix(cluster.sources)}</Field>
      <Field label="Verdicts">{formatMix(cluster.verdicts)}</Field>
      <Field label="First seen">{formatDateTime(cluster.first_seen_at)}</Field>
      <Field label="Last seen">{formatDateTime(cluster.last_seen_at)}</Field>
    </dl>
    <p className="mt-3 break-all font-mono text-xs text-muted-foreground">
      {cluster.cluster_key}
    </p>
  </Section>
);

export const PhrasesSection = ({ phrases }) => (
  <Section title={`Member phrases (${(phrases || []).length})`}>
    {(phrases || []).length === 0 ? (
      <p className="text-sm text-muted-foreground">No phrases recorded.</p>
    ) : (
      <ul className="list-disc space-y-1 pl-5 text-sm">
        {phrases.map((phrase) => (
          <li key={phrase}>“{phrase}”</li>
        ))}
      </ul>
    )}
  </Section>
);

export const SafetyFlagsSection = ({ flags }) =>
  (flags || []).length === 0 ? null : (
    <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
      <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
      <div>
        <span className="font-medium">Safety flags: </span>
        {flags.map((flag) => (
          <Badge key={flag} variant="warning" className="mr-1">
            {flag.replace(/_/g, " ")}
          </Badge>
        ))}
      </div>
    </div>
  );

export const NearestSection = ({ cluster }) => {
  const rows = nearestDialogues(cluster);
  return (
    <Section
      title="Nearest canonical Dialogues"
      description="The closest existing Dialogues retrieval found, and why each fell short."
    >
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No nearby Dialogue was found.</p>
      ) : (
        <ul className="divide-y">
          {rows.map((row) => (
            <li key={row.id} className="py-2 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to={dialogueHref(row.id)}
                  className="font-mono text-xs text-primary underline-offset-2 hover:underline"
                >
                  {row.id}
                </Link>
                {row.score != null && (
                  <span className="text-xs text-muted-foreground">
                    score {row.score.toFixed(2)}
                  </span>
                )}
                {eligibilityBadges(row.eligibility).map((badge) => (
                  <Badge key={badge.key} variant={badge.variant}>
                    {badge.label}
                  </Badge>
                ))}
              </div>
              {row.reasons.length > 0 && (
                <ul className="mt-1 list-disc pl-5 text-xs text-muted-foreground">
                  {row.reasons.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
};

export const SampleSignalsSection = ({ signals }) => (
  <Section title={`Sample signals (${(signals || []).length})`}>
    {(signals || []).length === 0 ? (
      <p className="text-sm text-muted-foreground">No sample signals.</p>
    ) : (
      <ul className="divide-y">
        {signals.map((signal) => (
          <li key={signal.signal_id} className="py-2 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline">{signal.source || "—"}</Badge>
              <Badge variant={signal.verdict === "RETRIEVAL_BUG" ? "destructive" : "secondary"}>
                {signal.verdict || "—"}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {formatDateTime(signal.created_at)}
              </span>
            </div>
            {signal.signature?.summary_phrase && (
              <p className="mt-1">“{signal.signature.summary_phrase}”</p>
            )}
            {signal.verdict_reason && (
              <p className="mt-1 text-xs text-muted-foreground">{signal.verdict_reason}</p>
            )}
          </li>
        ))}
      </ul>
    )}
  </Section>
);

export const HistorySection = ({ history }) => (
  <Section title="Triage history">
    {(history || []).length === 0 ? (
      <p className="text-sm text-muted-foreground">Not triaged yet.</p>
    ) : (
      <ol className="space-y-3">
        {[...history].reverse().map((entry, index) => (
          <li key={`${entry.at}-${index}`} className="text-sm">
            <div className="flex flex-wrap items-center gap-2">
              {entry.from && <ClusterStatusBadge status={entry.from} />}
              <span aria-hidden="true">→</span>
              <ClusterStatusBadge status={entry.to} />
              <span className="text-xs text-muted-foreground">
                {entry.by || "—"} · {formatDateTime(entry.at)}
              </span>
            </div>
            {entry.note && <p className="mt-1">{entry.note}</p>}
            {entry.linked_dialogue_id && (
              <p className="mt-1 text-xs">
                Linked{" "}
                <Link
                  to={dialogueHref(entry.linked_dialogue_id)}
                  className="font-mono text-primary hover:underline"
                >
                  {entry.linked_dialogue_id}
                </Link>
              </p>
            )}
          </li>
        ))}
      </ol>
    )}
  </Section>
);

export const TraceabilitySection = ({ traceability }) => {
  const steps = traceabilitySteps(traceability);
  return (
    <Section
      title="Traceability"
      description="Research review → authored Dialogue → forensic QA → certification → release."
    >
      {steps.length === 0 ? (
        <p className="text-sm text-muted-foreground">No stages recorded yet.</p>
      ) : (
        <ol className="relative space-y-3 border-l pl-4">
          {steps.map((step) => (
            <li key={step.stage} className="relative text-sm">
              <span className="absolute -left-[22px] top-1.5 h-3 w-3 rounded-full bg-primary" />
              <div className="font-medium">{step.label}</div>
              {step.ref &&
                (step.stage === "authored_dialogue_id" ? (
                  <Link
                    to={dialogueHref(step.ref)}
                    className="font-mono text-xs text-primary hover:underline"
                  >
                    {step.ref}
                  </Link>
                ) : (
                  <div className="font-mono text-xs">{step.ref}</div>
                ))}
              {(step.by || step.at) && (
                <div className="text-xs text-muted-foreground">
                  {step.by || "—"} · {formatDateTime(step.at)}
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
    </Section>
  );
};
